<script setup>
import { ref, getCurrentInstance } from 'vue'
import { notifyDialog, notifyDialogError } from '../composables/useDialogNotice'
import { DriverStore } from '../pinia/Driver.js'

const Driver = DriverStore()
const { proxy } = getCurrentInstance()

const raceAgainEnabled = ref(Driver.user.raceAgainEnabled == 1 || Driver.user.raceAgainEnabled === undefined)
const raceAgainMode = ref(parseInt(Driver.user.raceAgainMode) || 0)

const raceAgainModeOptions = [
    {
        value: 0,
        title: 'Random Race',
        label: 'Random Race',
        description: 'Start a new random race each time'
    },
    {
        value: 1,
        title: 'Same Race',
        label: 'Same Race',
        description: 'Repeat the exact same race'
    }
]

async function save() {
    let saved = false

    const raceAgainEnabledValue = raceAgainEnabled.value ? 1 : 0
    if(raceAgainEnabledValue != Driver.user.raceAgainEnabled) {
        try {
            await proxy.$http.post("", JSON.stringify({
                serviceName: "account",
                methodName: "UpdateRaceAgainEnabled",
                parameters: [raceAgainEnabledValue]
            }))

            Driver.user.raceAgainEnabled = raceAgainEnabledValue
            saved = true
        } catch (err) {
            notifyDialogError(err, 'Error saving Race Again enabled setting')
            console.error('UpdateRaceAgainEnabled error:', err)
            return
        }
    }

    const raceAgainModeValue = parseInt(raceAgainMode.value)
    if(raceAgainModeValue != parseInt(Driver.user.raceAgainMode || 0)) {
        try {
            await proxy.$http.post("", JSON.stringify({
                serviceName: "account",
                methodName: "UpdateRaceAgainMode",
                parameters: [raceAgainModeValue]
            }))

            Driver.user.raceAgainMode = raceAgainModeValue
            saved = true
        } catch (err) {
            notifyDialogError(err, 'Error saving Race Again mode setting')
            console.error('UpdateRaceAgainMode error:', err)
            return
        }
    }

    if (saved) {
        notifyDialog({ type: 'success', message: 'Your game settings have been saved.' })
    }
}
</script>

<template><div class="settings-tab-page gameSettingsForm">
    <div class="settings-tab-header">
        <div class="header-icon">
            <v-icon size="28">mdi-controller</v-icon>
        </div>
        <div class="header-content">
            <h3 class="page-title">Game Settings</h3>
            <p class="page-subtitle">Configure in-game preferences</p>
        </div>
    </div>

    <v-form>
        <div class="section-list">
            <div class="section-row">
                <div class="section-body">
                    <div class="setting-item">
                        <div class="setting-info">
                            <div class="setting-label">
                                <v-icon size="20" class="mr-2">mdi-restart</v-icon>
                                Race Again
                            </div>
                            <div class="setting-description">
                                Allow quick restart after completing a race
                            </div>
                        </div>
                        <v-switch v-model="raceAgainEnabled" color="primary" hide-details density="comfortable" class="setting-switch"></v-switch>
                    </div>

                    <v-divider class="my-4"></v-divider>

                    <div class="setting-item">
                        <div class="setting-info full-width">
                            <div class="setting-label">
                                <v-icon size="20" class="mr-2">mdi-racing-helmet</v-icon>
                                Race Again Mode
                            </div>
                            <div class="setting-description">
                                Choose how Race Again behaves
                            </div>
                            <div class="mode-options" :class="{ disabled: !raceAgainEnabled }">
                                <div v-for="option in raceAgainModeOptions" :key="option.value" class="mode-option" :class="{
                                        active: raceAgainMode === option.value,
                                        disabled: !raceAgainEnabled
                                    }" @click="raceAgainEnabled ? raceAgainMode = option.value : null">
                                    <div class="mode-radio">
                                        <v-icon v-if="raceAgainMode === option.value" size="18">mdi-radiobox-marked</v-icon>
                                        <v-icon v-else size="18">mdi-radiobox-blank</v-icon>
                                    </div>
                                    <div class="mode-content">
                                        <div class="mode-icon">
                                            <v-icon size="24">{{ option.value === 0 ? 'mdi-shuffle-variant' : 'mdi-replay' }}</v-icon>
                                        </div>
                                        <div class="mode-text">
                                            <div class="mode-title">{{ option.title }}</div>
                                            <div class="mode-desc">{{ option.description }}</div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <v-btn class="save-btn" variant="elevated" color="primary" size="large" @click="save" block>
            <v-icon class="mr-2">mdi-content-save</v-icon>
            Save Settings
        </v-btn>
    </v-form>
</div></template>

<style scoped>
.mode-options {
  display:flex;
  flex-direction:column;
  gap:12px;
  margin-top:12px}
.mode-options.disabled {
  opacity:.4;
  pointer-events:none}
.mode-option {
  display:flex;
  align-items:center;
  gap:12px;
  padding:16px;
  background:var(--glass);
  border:2px solid rgba(255,255,255,.1);
  border-radius:10px;
  cursor:pointer;
  transition:all .2s ease}
.mode-option:hover:not(.disabled) {
  background:var(--glass-strong);
  border-color:rgba(var(--v-theme-primary),.5);
  transform:translate(4px)}
.mode-option.active {
  background:rgba(var(--v-theme-primary),.15);
  border-color:rgb(var(--v-theme-primary))}
.mode-option.disabled {
  cursor:not-allowed}
.mode-radio {
  display:flex;
  align-items:center}
.mode-radio .v-icon {
  color:var(--ink-soft)}
.mode-option.active .mode-radio .v-icon {
  color:rgb(var(--v-theme-primary))!important}
.mode-content {
  display:flex;
  align-items:center;
  gap:16px;
  flex:1}
.mode-icon {
  display:flex;
  align-items:center;
  justify-content:center;
  width:40px;
  height:40px;
  background:var(--glass-strong);
  border-radius:8px}
.mode-icon .v-icon {
  color:var(--ink);
  transition:color .2s ease}
.mode-option.active .mode-icon {
  background:rgba(var(--v-theme-primary),.2)}
.mode-option.active .mode-icon .v-icon {
  color:rgb(var(--v-theme-primary))!important}
.mode-text {
  flex:1}
.mode-title {
  font-size:1em;
  font-weight:500;
  color:var(--ink);
  margin-bottom:4px}
.mode-desc {
  font-size:.8em;
  color:var(--mute)}
</style>
